// ============================================================
// useFinder — 予約 + 車両コード検索ロジック
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import { Reservation, Vehicle } from './types';

interface UseFinderProps {
  reservations: Reservation[];
  vehicles: Vehicle[];
}

export interface FinderResult {
  type: 'reservation' | 'vehicle';
  reservation?: Reservation;
  vehicle?: Vehicle;
  vehicleIndex?: number;
}

interface UseFinderReturn {
  query: string;
  setQuery: (q: string) => void;
  results: FinderResult[];
  currentIndex: number;
  /** 後方互換: 現在の予約結果（ハイライト用） */
  currentReservation: Reservation | null;
  /** 現在の車両結果（スクロール用） */
  currentVehicle: { vehicle: Vehicle; index: number } | null;
  search: () => void;
  next: () => void;
  prev: () => void;
  clear: () => void;
}

// 結果アイテムの安定したキーを生成（予約ID or 車両ID）
function resultKey(item: FinderResult): string {
  if (item.type === 'reservation') return `r:${item.reservation!.id}`;
  return `v:${item.vehicle!.id}`;
}

export function useFinder({ reservations, vehicles }: UseFinderProps): UseFinderReturn {
  const [query, setQueryState] = useState('');
  // インデックスではなくIDで追跡することで、reservations配列の更新時に別アイテムへジャンプしない
  const [currentId, setCurrentId] = useState<string | null>(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const out: FinderResult[] = [];

    // 車両コード検索（vehicle_code, vehicle_number）
    vehicles.forEach((v, idx) => {
      if (
        (v.vehicle_code || '').toLowerCase().includes(q) ||
        (v.vehicle_number || '').toLowerCase().includes(q)
      ) {
        out.push({ type: 'vehicle', vehicle: v, vehicleIndex: idx });
      }
    });

    // 予約検索（顧客名、予約コード）
    reservations.forEach(r => {
      if (
        (r.customer_name || '').toLowerCase().includes(q) ||
        (r.contact_code || '').toLowerCase().includes(q)
      ) {
        out.push({ type: 'reservation', reservation: r });
      }
    });

    return out;
  }, [reservations, vehicles, query]);

  // currentIdからインデックスを導出（results再計算後も同じアイテムを指す）
  const currentIndex = useMemo(() => {
    if (currentId === null || results.length === 0) return 0;
    const idx = results.findIndex(r => resultKey(r) === currentId);
    return idx >= 0 ? idx : 0;
  }, [results, currentId]);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    setCurrentId(null);
  }, []);

  const search = useCallback(() => {
    setCurrentId(null);
  }, []);

  const next = useCallback(() => {
    if (results.length === 0) return;
    setCurrentId(prev => {
      const idx = prev ? results.findIndex(r => resultKey(r) === prev) : -1;
      const nextIdx = (idx + 1) % results.length;
      return resultKey(results[nextIdx]);
    });
  }, [results]);

  const prev = useCallback(() => {
    if (results.length === 0) return;
    setCurrentId(prev => {
      const idx = prev ? results.findIndex(r => resultKey(r) === prev) : 0;
      const prevIdx = (idx - 1 + results.length) % results.length;
      return resultKey(results[prevIdx]);
    });
  }, [results]);

  const clear = useCallback(() => {
    setQueryState('');
    setCurrentId(null);
  }, []);

  const current = results[currentIndex] ?? null;
  const currentReservation = current?.type === 'reservation' ? current.reservation! : null;
  const currentVehicle = current?.type === 'vehicle'
    ? { vehicle: current.vehicle!, index: current.vehicleIndex! }
    : null;

  return {
    query, setQuery, results, currentIndex,
    currentReservation, currentVehicle,
    search, next, prev, clear,
  };
}
